import { useContext, useEffect, useMemo, useState } from 'react';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, Tooltip } from '@mui/material';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { OptionType } from 'components/CustomSelect/types';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import SelectValueViewer from 'components/SelectValueViewer';
import { RELATED_ENTITY_ALLOCATION_TOOLTIP_TITLE } from 'pages/CapTable/fund-ownership/constants';

const EntityValueViewer = (props: any) => {
  const { cell } = props;
  const [availableEntities, setAvailableEntities] = useState<OptionType[]>([]);
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext) as any;

  useEffect(() => {
    if (!companyInfo) {
      return;
    }
    const tempEntities = [
      {
        id: String(companyInfo.id),
        value: String(companyInfo.id),
        label: companyInfo.name || '',
      },
      ...(companyInfo?.child_companies || []).map((child: any) => ({
        id: String(child.company_id),
        value: String(child.company_id),
        label: child.company_name || '',
      })),
    ];

    if (availableEntities.length === 0) {
      setAvailableEntities(tempEntities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);

  useEffect(() => {
    if (!cell.value && companyInfo) {
      onCellsChanged([{ cell, value: companyInfo.id }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell.value]);

  const isChildCompany = useMemo(() => {
    if (!companyInfo?.child_companies || !cell?.value) {
      return false;
    }
    return (companyInfo.child_companies as any[]).some(
      child =>
        (`${child.company_id}` === `${cell.value}` || child.company_name === cell.value) &&
        `${child.company_id}` !== `${companyInfo.id}`
    );
  }, [companyInfo, cell?.value]);

  if (availableEntities.length === 0) {
    return null;
  }

  if (!isChildCompany) {
    return <SelectValueViewer {...props} options={availableEntities} />;
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <SelectValueViewer {...props} options={availableEntities} />
      <Tooltip title={RELATED_ENTITY_ALLOCATION_TOOLTIP_TITLE} arrow>
        <InfoOutlined
          data-testid="related-entity-allocation-indicator"
          fontSize="inherit"
          sx={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 14,
            color: 'info.main',
            cursor: 'help',
            zIndex: 1,
          }}
        />
      </Tooltip>
    </Box>
  );
};

export default EntityValueViewer;
